import React, { useEffect, useState } from "react";
import {
  Box, Flex, Text, useToast, Spinner, SimpleGrid, Stat, StatLabel, StatNumber,
  Button, Heading, Table, Thead, Tbody, Tr, Th, Td,
} from "@chakra-ui/react";
import { getMetrics } from "../../utils/apis/agentApi";

/** Human labels for the agent's metric keys, so the page reads like a report
 *  instead of raw JSON. Anything unmapped falls back to a de-underscored key. */
const LABELS: Record<string, string> = {
  intros_created: "Intros created",
  avg_rating: "Average rating",
  followup_sent: "Follow-ups sent",
  followup_rated: "Follow-ups rated",
  followup_response_rate: "Follow-up response rate",
  no_worker_found_total: "No worker found",
  no_worker_found_service_not_on_platform: "No worker: service not offered",
  no_worker_found_no_providers: "No worker: none signed up yet",
  no_worker_found_location: "No worker: none in that area",
  csat_sent: "CSAT asked",
  csat_rated: "CSAT answered",
  csat_avg_rating: "CSAT average",
  csat_response_rate: "CSAT response rate",
  today_spend_usd: "LLM spend today (USD)",
  spend_cap_usd: "Daily spend cap (USD)",
  wa_spend_today_usd: "WhatsApp spend today (USD)",
};

const pretty = (k: string) => LABELS[k] || k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
const fmt = (v: any) => {
  if (v === null || v === undefined) return "-";
  if (typeof v === "number") return Number.isInteger(v) ? v : v.toFixed(2);
  return String(v);
};

const StatCards = ({ data }: { data: Record<string, any> }) => (
  <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={3}>
    {Object.entries(data)
      .filter(([, v]) => typeof v !== "object" || v === null)
      .map(([k, v]) => (
        <Stat key={k} p={3} borderWidth="1px" borderRadius="md" backgroundColor="admin.surface">
          <StatLabel fontSize="xs" color="admin.muted">{pretty(k)}</StatLabel>
          <StatNumber fontSize="lg" color="admin.accent">{fmt(v)}</StatNumber>
        </Stat>
      ))}
  </SimpleGrid>
);

/** Agent weekly metrics, rendered as readable cards + a top-categories table. */
const AgentMetrics = () => {
  const [m, setM] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    const res = await getMetrics();
    if (res.ok && res.data) setM(res.data);
    else toast({ title: res.status === 502 ? "Agent unreachable" : "Failed to load",
      status: "error", duration: 3000, isClosable: true });
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const thisWeek = (m?.this_week || {}) as Record<string, any>;
  const lastWeek = (m?.last_week || {}) as Record<string, any>;
  const topCats = (thisWeek.top_requested_categories || []) as any[];
  // `note` is internal developer commentary about how some numbers are derived —
  // not something the admin needs, so it's excluded from the cards entirely.
  const topline = m
    ? Object.fromEntries(Object.entries(m).filter(
        ([k, v]) => k !== "note" && (typeof v !== "object" || v === null)))
    : {};

  return (
    <Box p={5}>
      <Flex justifyContent="space-between" mb={4} alignItems="center">
        <Box>
          <Heading size="md">Agent metrics</Heading>
          <Text fontSize="sm" color="admin.muted">
            How the WhatsApp agent performed: conversations it handled, how often it
            found a worker, and what it cost.
          </Text>
        </Box>
        <Button colorScheme="purple" backgroundColor="#88077b" onClick={load}>Refresh</Button>
      </Flex>

      {loading ? <Spinner size="xl" /> : !m ? <Text color="admin.muted">No metrics.</Text> : (
        <>
          {Object.keys(topline).length > 0 && (
            <Box mb={6}><Heading size="sm" mb={2}>Spend</Heading><StatCards data={topline} /></Box>
          )}

          <Box mb={6}>
            <Heading size="sm" mb={2}>This week</Heading>
            {Object.keys(thisWeek).length ? <StatCards data={thisWeek} />
              : <Text color="admin.muted">Nothing yet this week.</Text>}
          </Box>

          {topCats.length > 0 && (
            <Box mb={6}>
              <Heading size="sm" mb={2}>Most requested trades</Heading>
              <Table variant="striped" size="sm" maxW="480px">
                <Thead backgroundColor="#88077b"><Tr>
                  <Th color="white">Trade</Th><Th color="white" isNumeric>Requests</Th>
                </Tr></Thead>
                <Tbody>
                  {topCats.map((c: any, i: number) => (
                    <Tr key={c.category} backgroundColor={i % 2 === 0 ? "admin.surface" : "admin.selected"}>
                      <Td textTransform="capitalize">{c.category}</Td><Td isNumeric>{c.count}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          <Box mb={6}>
            <Heading size="sm" mb={2}>Last week</Heading>
            {Object.keys(lastWeek).length ? <StatCards data={lastWeek} />
              : <Text color="admin.muted">No data.</Text>}
          </Box>
        </>
      )}
    </Box>
  );
};
export default AgentMetrics;
